"use client";

import React from "react";
import { ArrowLeft, Globe, Mail, Phone, Building2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

function BusinessDirectoryConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 h-32 w-48 animate-pulse rounded-lg bg-muted"></div>
        <div className="mb-4 h-12 w-3/4 animate-pulse rounded-lg bg-muted"></div>
        <div className="mb-8 flex gap-2">
          <div className="h-6 w-24 animate-pulse rounded-full bg-muted"></div>
          <div className="h-6 w-32 animate-pulse rounded-full bg-muted"></div>
        </div>
        <div className="mb-12 space-y-3">
          <div className="h-4 w-full animate-pulse rounded bg-muted"></div>
          <div className="h-4 w-full animate-pulse rounded bg-muted"></div>
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted"></div>
        </div>
        <div className="h-48 w-full animate-pulse rounded-xl bg-muted"></div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col items-center justify-center px-4 py-24 text-center sm:px-6 lg:px-8">
        <Building2 className="mb-4 size-16 text-muted-foreground/50" />
        <h2 className="mb-2 font-default text-2xl font-semibold text-foreground">Business Not Found</h2>
        <p className="mb-8 text-muted-foreground">The business directory entry you are looking for does not exist or has been removed.</p>
        <Link to="/BusinessHubPage">
          <Button variant="default">Return to Business Hub</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const cf = post.customFields || {};

  const name = cf.business_name || post.title;
  const description = cf.business_description || post.content || post.excerpt;
  const logo = cf.business_logo || post.featuredImage;
  const sponsorLevel = cf.sponsor_level;
  const categories = cf.business_categories;

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
      {}
      {logo && (
        <div className="mb-10 flex justify-start">
          <img
            src={logo}
            alt={`${name} logo`}
            className="max-h-40 w-auto object-contain drop-shadow-sm"
          />
        </div>
      )}

      {}
      <div className="mb-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <h1 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
          {name}
        </h1>
        {sponsorLevel && sponsorLevel !== "null" && (
          <Badge className="bg-primary px-3 py-1 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-sm">
            {sponsorLevel}
          </Badge>
        )}
      </div>

      {}
      {categories && (
        <div className="mb-10 flex flex-wrap gap-2">
          {categories.split(',').map((category: string, index: number) => (
            <Badge key={index} variant="secondary" className="px-3 py-1 text-sm">
              {category.trim()}
            </Badge>
          ))}
        </div>
      )}

      <Separator className="mb-10" />

      {}
      {description && (
        <div className="prose prose-lg dark:prose-invert mb-16 max-w-none text-muted-foreground">
          <div className="whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: description }} />
        </div>
      )}

      {}
      {(cf.contact_phone || cf.contact_email || cf.website_url) && (
        <Card className="mb-16 border-primary/20 bg-card/50 shadow-lg backdrop-blur-sm">
          <CardHeader className="border-b border-border/50 bg-muted/30 pb-4">
            <CardTitle className="font-default text-2xl text-foreground">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 pt-6 sm:grid-cols-2 lg:grid-cols-3">
            {cf.contact_phone && (
              <Link to={`tel:${cf.contact_phone}`}>
                <div className="group flex items-center gap-4 rounded-lg border border-transparent p-3 transition-colors hover:border-primary/30 hover:bg-primary/5">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform group-hover:scale-110">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Phone</p>
                    <p className="font-semibold text-foreground">{cf.contact_phone}</p>
                  </div>
                </div>
              </Link>
            )}

            {cf.contact_email && (
              <Link to={`mailto:${cf.contact_email}`}>
                <div className="group flex items-center gap-4 rounded-lg border border-transparent p-3 transition-colors hover:border-primary/30 hover:bg-primary/5">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform group-hover:scale-110">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="truncate font-semibold text-foreground">{cf.contact_email}</p>
                  </div>
                </div>
              </Link>
            )}

            {cf.website_url && (
              <Link to={cf.website_url} newTab>
                <div className="group flex items-center gap-4 rounded-lg border border-transparent p-3 transition-colors hover:border-primary/30 hover:bg-primary/5">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform group-hover:scale-110">
                    <Globe className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Website</p>
                    <p className="font-semibold text-primary group-hover:underline">Visit Site</p>
                  </div>
                </div>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {}
      <div className="flex justify-center pt-8">
        <Link to="/BusinessHubPage">
          <Button variant="outline" size="lg" className="gap-2 border-primary/50 hover:bg-primary/10 hover:text-primary">
            <ArrowLeft className="size-4" />
            Back to Business Hub
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function SingleBusinessDirectoryTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'business_directory' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-business-directory-template-section" className="min-h-screen bg-background">
      <PostsProvider wp_query={wp_query}>
        <BusinessDirectoryConsumer />
      </PostsProvider>
    </section>
  );
}